// MEO landmark ads (Phase 1, Step 4-6, 2026-05-01):
// 商業ポストカード CV ダッシュボード。
//
// + T1-A R1 (2026-05-06): 「月次レポート」タブを追加。過去 12 ヶ月の
//   monthly_meo_reports を card 表示 + 詳細 modal + ブラウザ印刷で PDF 保存。
//   Spec: design/seo-landmark-spec.md §1.4.4 / §5.2
//   Backend: omikuji-api PR #94 (= GET /system/business/monthly-meo-reports)
//
// auth: ADMIN (全 advertiser) / BUSINESS (自分が advertiser_user_id の items のみ)
// Phase 1 PoC 簡略化: impressions / kyo_tied / by_language は null 固定 (Phase 2 で対応予定)

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/common/Tabs';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    MonthlyMeoLandmark,
    MonthlyMeoReportItem
} from '@/stores/BusinessDashboardStore';
import { FORTUNE_LABEL, MOTIF_LABEL } from '@/utils/omikujiLabels';
import { todayIso, daysAgoIso } from '@/utils/utils';

const formatPercent = (rate: number | null | undefined) => {
    if (rate == null) return '-';
    return `${(rate * 100).toFixed(1)}%`;
};

const formatNumber = (n: number | null | undefined) => {
    if (n == null) return '-';
    return n.toLocaleString();
};

const formatYearMonth = (ym: string) => {
    const [y, m] = ym.split('-');
    return `${y} 年 ${Number(m)} 月`;
};

const formatDateTime = (iso: string | null | undefined) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export default observer(function BusinessDashboardPage() {
    const [tab, setTab] = useState<'cv' | 'monthly'>('cv');

    return (
        <div className='space-y-4'>
            <Tabs value={tab} onValueChange={v => setTab(v as 'cv' | 'monthly')}>
                <TabsList variant='userTab' className='flex'>
                    <TabsTrigger variant='userTab' value='cv' className='flex-1'>
                        CV 集計
                    </TabsTrigger>
                    <TabsTrigger variant='userTab' value='monthly' className='flex-1'>
                        月次レポート
                    </TabsTrigger>
                </TabsList>
                <TabsContent value='cv' className='mt-4'>
                    <CvTab />
                </TabsContent>
                <TabsContent value='monthly' className='mt-4'>
                    <MonthlyReportsTab />
                </TabsContent>
            </Tabs>
        </div>
    );
});

// =============================================================================
// CV 集計タブ (= 既存ダッシュボード、表示内容変更なし)
// =============================================================================

const CvTab = observer(function CvTab() {
    const { t } = useTranslation();
    const {
        businessDashboardStore: { summary, loading, fetchSummary }
    } = useStore();

    const [from, setFrom] = useState<string>(daysAgoIso(30));
    const [to, setTo] = useState<string>(todayIso());

    const reload = async () => {
        const res = await flowResult(fetchSummary({ from, to }));
        if (!res?.ok) {
            toastify('error', res?.message || '集計取得に失敗しました');
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totals = summary?.totals;
    const byFortune: any[] = summary?.by_fortune ?? [];
    const byMotif: any[] = summary?.by_motif ?? [];
    const byLandmark: any[] = summary?.by_landmark ?? [];
    const periodLabel = summary?.period ?
        `${summary.period.from} 〜 ${summary.period.to}` :
        `${from} 〜 ${to}`;

    return (
        <div className='space-y-4'>
            {/* 期間フィルタ */}
            <div className='layout-card'>
                <div className='flex flex-wrap items-end gap-3'>
                    <div className='flex flex-col gap-1'>
                        <label className='text-sm font-medium'>開始日</label>
                        <Input
                            type='date'
                            value={from}
                            onChange={e => setFrom(e.target.value)}
                            className='w-[160px]'
                        />
                    </div>
                    <div className='flex flex-col gap-1'>
                        <label className='text-sm font-medium'>終了日</label>
                        <Input
                            type='date'
                            value={to}
                            onChange={e => setTo(e.target.value)}
                            className='w-[160px]'
                        />
                    </div>
                    <Button variant='active' onClick={reload} disabled={loading}>
                        {loading ? '読み込み中…' : t('button.search') || '検索'}
                    </Button>
                    <span className='text-sm text-gray-500 ml-auto'>
                        {`対象期間: ${periodLabel}`}
                    </span>
                </div>
            </div>

            {/* totals サマリー */}
            <div className='layout-card'>
                <h2 className='text-[16px] font-bold mb-3'>合計指標</h2>
                {!totals ?
                    (
                        <div className='text-gray-500 py-4'>データがありません</div>
                    ) :
                    (
                        <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                            <SummaryCard
                                label='抽選回数 (drawn)'
                                value={String(totals.drawn ?? 0)}
                                hint='draw-omikuji 件数'
                            />
                            <SummaryCard
                                label='取得 (collected)'
                                value={String(totals.collected ?? 0)}
                                hint='Phase 1 では drawn と同値'
                            />
                            <SummaryCard
                                label='来店 CV (used)'
                                value={String(totals.used ?? 0)}
                                hint='use-item 件数'
                            />
                            <SummaryCard
                                label='CV 率'
                                value={formatPercent(totals.cv_rate)}
                                hint='used / drawn'
                            />
                        </div>
                    )}
                {totals && (totals.impressions == null || summary?.kyo_tied == null) && (
                    <p className='text-xs text-gray-500 mt-3'>
                        ※ impressions / kyo_tied / by_language は Phase 2 (omikuji_events 追加) で対応予定 (現在 null)
                    </p>
                )}
            </div>

            {/* 軸別 breakdown */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <BreakdownTable
                    title='占い結果別 (fortune)'
                    rows={byFortune.map(r => ({
                        key: String(r.fortune),
                        label: FORTUNE_LABEL[r.fortune] ?? `#${r.fortune}`,
                        count: r.count,
                        rate: r.cv_rate
                    }))}
                />
                <BreakdownTable
                    title='モチーフ別 (motif)'
                    rows={byMotif.map(r => ({
                        key: r.motif,
                        label: MOTIF_LABEL[r.motif] ?? r.motif,
                        count: r.count,
                        rate: r.cv_rate
                    }))}
                />
                <div className='md:col-span-2'>
                    <LandmarkTable rows={byLandmark} />
                </div>
            </div>
        </div>
    );
});

const SummaryCard = ({ label, value, hint }: { label: string, value: string, hint?: string }) => (
    <div className='border rounded-md p-3'>
        <div className='text-xs text-gray-500'>{label}</div>
        <div className='text-2xl font-bold mt-1'>{value}</div>
        {hint && <div className='text-xs text-gray-400 mt-1'>{hint}</div>}
    </div>
);

interface BreakdownRow {
    key: string,
    label: string,
    count: number,
    rate: number
}
const BreakdownTable = ({ title, rows }: { title: string, rows: BreakdownRow[] }) => (
    // PoC (2026-05-02 user #6): ブラウザリサイズで table が崩れる問題対応。
    // - 親 layout-card は min-w-0 で grid 親の最小幅制約を解除 (= 子は親に追従して縮む)
    // - 内側 table は overflow-x-auto wrapper で横スクロール可、min-w で列幅維持
    <div className='layout-card min-w-0'>
        <h3 className='text-[15px] font-bold mb-2'>{title}</h3>
        {rows.length === 0 ?
            (
                <div className='text-gray-500 py-2 text-sm'>データなし</div>
            ) :
            (
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm min-w-[280px]'>
                        <thead>
                            <tr className='border-b border-gray-200 text-left'>
                                <th className='py-2 px-2 whitespace-nowrap'>区分</th>
                                <th className='py-2 px-2 text-right whitespace-nowrap'>件数</th>
                                <th className='py-2 px-2 text-right whitespace-nowrap'>CV 率</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.key} className='border-b border-gray-100'>
                                    <td className='py-2 px-2 whitespace-nowrap'>{r.label}</td>
                                    <td className='py-2 px-2 text-right font-mono whitespace-nowrap'>{r.count}</td>
                                    <td className='py-2 px-2 text-right font-mono whitespace-nowrap'>{formatPercent(r.rate)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
    </div>
);

const LandmarkTable = ({ rows }: { rows: any[] }) => (
    // PoC (2026-05-02 user #6): 5 列あるため特に narrow viewport で崩れやすかった。
    // overflow-x-auto + min-w-[480px] で確実に横スクロール対応 (小幅 PC でも全列表示維持)。
    <div className='layout-card min-w-0'>
        <h3 className='text-[15px] font-bold mb-2'>ランドマーク別 (drawn / used)</h3>
        {rows.length === 0 ?
            (
                <div className='text-gray-500 py-2 text-sm'>データなし</div>
            ) :
            (
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm min-w-[480px]'>
                        <thead>
                            <tr className='border-b border-gray-200 text-left'>
                                <th className='py-2 px-2 whitespace-nowrap'>landmark id</th>
                                <th className='py-2 px-2 whitespace-nowrap'>名称</th>
                                <th className='py-2 px-2 text-right whitespace-nowrap'>drawn</th>
                                <th className='py-2 px-2 text-right whitespace-nowrap'>used</th>
                                <th className='py-2 px-2 text-right whitespace-nowrap'>CV 率</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const cv = r.drawn > 0 ? r.used / r.drawn : 0;
                                return (
                                    <tr key={r.landmark_id} className='border-b border-gray-100'>
                                        <td className='py-2 px-2 text-gray-500 whitespace-nowrap'>{r.landmark_id}</td>
                                        <td className='py-2 px-2'>{r.name ?? '-'}</td>
                                        <td className='py-2 px-2 text-right font-mono whitespace-nowrap'>{r.drawn}</td>
                                        <td className='py-2 px-2 text-right font-mono whitespace-nowrap'>{r.used}</td>
                                        <td className='py-2 px-2 text-right font-mono whitespace-nowrap'>{formatPercent(cv)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
    </div>
);

// =============================================================================
// 月次レポートタブ (T1-A R1, 2026-05-06)
// =============================================================================

const MonthlyReportsTab = observer(function MonthlyReportsTab() {
    const {
        businessDashboardStore: {
            monthlyReportsData,
            monthlyReportsLoading,
            fetchMonthlyReports
        }
    } = useStore();

    const [selected, setSelected] = useState<{
        report: MonthlyMeoReportItem,
        landmark: MonthlyMeoLandmark | undefined
    } | null>(null);

    useEffect(() => {
        const load = async () => {
            const res = await flowResult(fetchMonthlyReports());
            if (res && !res.ok) {
                toastify('error', res?.message || '月次レポート取得に失敗しました');
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const landmarksById = useMemo(() => {
        const map = new Map<number, MonthlyMeoLandmark>();
        (monthlyReportsData?.landmarks ?? []).forEach(l => map.set(l.id, l));
        return map;
    }, [monthlyReportsData]);

    const grouped = useMemo(() => {
        const reports = monthlyReportsData?.reports ?? [];
        const map = new Map<string, MonthlyMeoReportItem[]>();
        reports.forEach((r) => {
            const arr = map.get(r.year_month) ?? [];
            arr.push(r);
            map.set(r.year_month, arr);
        });
        return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
    }, [monthlyReportsData]);

    if (monthlyReportsLoading && !monthlyReportsData) {
        return (
            <div className='layout-card'>
                <div className='text-gray-500 py-6 text-center'>読み込み中…</div>
            </div>
        );
    }

    const landmarks = monthlyReportsData?.landmarks ?? [];

    if (landmarks.length === 0) {
        return (
            <div className='layout-card'>
                <h2 className='text-[16px] font-bold mb-2'>月次 MEO レポート</h2>
                <div className='text-gray-500 py-6 text-sm'>
                    現在 sponsor として登録されている landmark がありません。
                    <div className='mt-2 text-xs text-gray-400'>
                        landmark の sponsor_tier を「standard」以上に設定すると、月初に自動でレポートが生成されます。
                    </div>
                </div>
            </div>
        );
    }

    if (grouped.length === 0) {
        return (
            <div className='layout-card'>
                <h2 className='text-[16px] font-bold mb-2'>月次 MEO レポート</h2>
                <div className='text-gray-500 py-6 text-sm'>
                    対象月のレポートはまだ生成されていません。
                    <div className='mt-2 text-xs text-gray-400'>
                        月次レポートは月初の cron で自動生成されます (= 過去 12 ヶ月分まで一覧表示)。
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='space-y-4'>
            <div className='layout-card'>
                <div className='flex items-baseline justify-between mb-2'>
                    <h2 className='text-[16px] font-bold'>月次 MEO レポート</h2>
                    <span className='text-xs text-gray-500'>
                        過去
                        {' '}
                        {monthlyReportsData?.months_back ?? 12}
                        {' '}
                        ヶ月分・
                        {landmarks.length}
                        {' '}
                        landmark
                    </span>
                </div>
                <p className='text-xs text-gray-500'>
                    各カードをクリックすると詳細を表示します。詳細画面の「印刷 / PDF 保存」でブラウザの印刷機能から PDF として保存できます。
                </p>
            </div>

            {grouped.map(([yearMonth, reports]) => (
                <div key={yearMonth} className='layout-card'>
                    <h3 className='text-[15px] font-bold mb-3'>{formatYearMonth(yearMonth)}</h3>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
                        {reports.map((r) => {
                            const lm = landmarksById.get(r.landmark_id);
                            return (
                                <ReportCard
                                    key={r.id}
                                    report={r}
                                    landmark={lm}
                                    onClick={() => setSelected({ report: r, landmark: lm })}
                                />
                            );
                        })}
                    </div>
                </div>
            ))}

            <Sheet open={!!selected} onOpenChange={open => !open && setSelected(null)}>
                <SheetContent
                    side='right'
                    className='w-full sm:max-w-2xl overflow-y-auto'
                >
                    {selected && (
                        <>
                            <SheetHeader>
                                <SheetTitle>
                                    {selected.landmark?.name ?? `landmark #${selected.report.landmark_id}`}
                                    <span className='ml-2 text-sm text-gray-500'>
                                        {formatYearMonth(selected.report.year_month)}
                                    </span>
                                </SheetTitle>
                                <SheetDescription>
                                    sponsor_tier:
                                    {' '}
                                    {selected.landmark?.sponsor_tier ?? '-'}
                                </SheetDescription>
                            </SheetHeader>
                            <div className='mt-4'>
                                <ReportDetail report={selected.report} landmark={selected.landmark} />
                            </div>
                            <div className='mt-4 flex gap-2 justify-end print:hidden'>
                                <Button variant='active' onClick={() => window.print()}>
                                    印刷 / PDF 保存
                                </Button>
                            </div>
                            <p className='mt-2 text-xs text-gray-400 print:hidden'>
                                ※ 印刷ダイアログで「PDF として保存」を選ぶと PDF ファイルとして保存できます。
                            </p>
                        </>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
});

const ReportCard = ({
    report,
    landmark,
    onClick
}: {
    report: MonthlyMeoReportItem,
    landmark: MonthlyMeoLandmark | undefined,
    onClick: () => void
}) => {
    const p = report.payload || {};
    const sc = p.search_console || null;
    const collected = p.collected || {};
    const issued = p.issued || {};
    return (
        <button
            type='button'
            onClick={onClick}
            className='border rounded-md p-3 text-left hover:bg-gray-50 transition-colors w-full'
        >
            <div className='font-semibold text-sm mb-2 truncate'>
                {landmark?.name ?? `landmark #${report.landmark_id}`}
            </div>
            <div className='grid grid-cols-2 gap-x-3 gap-y-1 text-xs'>
                <div className='text-gray-500'>検索クリック</div>
                <div className='font-mono text-right'>{formatNumber(sc?.clicks)}</div>
                <div className='text-gray-500'>表示回数</div>
                <div className='font-mono text-right'>{formatNumber(sc?.impressions)}</div>
                <div className='text-gray-500'>発行</div>
                <div className='font-mono text-right'>{formatNumber(issued.postcards)}</div>
                <div className='text-gray-500'>取得</div>
                <div className='font-mono text-right'>{formatNumber(collected.kept)}</div>
                <div className='text-gray-500'>クーポン使用</div>
                <div className='font-mono text-right'>{formatNumber(collected.used_coupon)}</div>
            </div>
            {report.email_sent_at && (
                <div className='mt-2 text-[10px] text-gray-400'>
                    送信済:
                    {' '}
                    {formatDateTime(report.email_sent_at)}
                </div>
            )}
        </button>
    );
};

const ReportDetail = ({
    report,
    landmark
}: {
    report: MonthlyMeoReportItem,
    landmark: MonthlyMeoLandmark | undefined
}) => {
    const p = report.payload || {};
    const sc = p.search_console;
    const issued = p.issued || {};
    const collected = p.collected || {};
    const compared = p.compared_to_prev_month;

    return (
        <div className='space-y-4 text-sm'>
            <DetailSection title='Search Console (Google 検索)'>
                {sc ?
                    (
                        <DetailGrid
                            rows={[
                                ['クリック数', formatNumber(sc.clicks)],
                                ['表示回数', formatNumber(sc.impressions)],
                                ['平均掲載順位', sc.avg_position != null ? sc.avg_position.toFixed(1) : '-']
                            ]}
                        />
                    ) :
                    (
                        <div className='text-gray-500 text-xs'>
                            Google Search Console 未連携 (= 環境変数 GOOGLE_SEARCH_CONSOLE_* 未設定または取得失敗)
                        </div>
                    )}
            </DetailSection>

            <DetailSection title='Omikuji 経由 (発行 / 取得 / クーポン)'>
                <DetailGrid
                    rows={[
                        ['発行 (postcards)', formatNumber(issued.postcards)],
                        ['取得 (kept)', formatNumber(collected.kept)],
                        ['クーポン使用 (used)', formatNumber(collected.used_coupon)]
                    ]}
                />
            </DetailSection>

            <DetailSection title='前月比'>
                {compared && compared.kept_delta_pct != null ?
                    (
                        <div className='text-sm'>
                            取得数 前月比:
                            {' '}
                            <span className={compared.kept_delta_pct >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {compared.kept_delta_pct >= 0 ? '+' : ''}
                                {compared.kept_delta_pct.toFixed(1)}
                                {' '}
                                %
                            </span>
                        </div>
                    ) :
                    (
                        <div className='text-gray-500 text-xs'>前月データなし (= 初月) または比較不可</div>
                    )}
            </DetailSection>

            <DetailSection title='参考情報'>
                <DetailGrid
                    rows={[
                        ['landmark_id', String(report.landmark_id)],
                        ['sponsor_tier', landmark?.sponsor_tier ?? '-'],
                        ['年月', report.year_month],
                        ['email 送信', report.email_sent_at ? formatDateTime(report.email_sent_at) ?? '-' : '未送信']
                    ]}
                />
            </DetailSection>
        </div>
    );
};

const DetailSection = ({ title, children }: { title: string, children: React.ReactNode }) => (
    <div>
        <h4 className='text-[14px] font-semibold mb-2 border-b pb-1'>{title}</h4>
        {children}
    </div>
);

const DetailGrid = ({ rows }: { rows: [string, string][] }) => (
    <div className='grid grid-cols-2 gap-x-3 gap-y-1'>
        {rows.map(([label, value]) => (
            <div className='contents' key={label}>
                <div className='text-gray-500'>{label}</div>
                <div className='font-mono text-right'>{value}</div>
            </div>
        ))}
    </div>
);
